/**
 * Project Store
 *
 * 백엔드 포트(services/backend)를 통한 프로젝트 상태 관리
 * - 프로젝트 CRUD 작업
 * - 모델 CRUD 작업 (MAIN-001, MDH-001)
 * - 사용자별 데이터 분리는 백엔드 구현체가 보장
 */

import { create } from 'zustand';
import { getBackend } from '@/app/services/backend';
import { isRestartMode } from '@/app/authoring/marsDeckRequest';
import { generateUuid } from '@/shared/uuid';
import type {
  Project,
  ProjectInsert,
  ProjectUpdate,
  ProjectSummary,
  ProjectData,
  Model,
  ModelInsert,
  ModelUpdate,
  ModelUpdateInput,
  VersionEntry,
} from '@/types/project';
import { roleFields } from '@/types/scope';

/** scope 정합 경고는 프로젝트당 세션에 한 번만 (내비게이션마다 반복 출력 방지) */
const scopeWarned = new Set<string>();
import { findModelById, validateProjectData, summarizeProject } from '@/app/persistence/projectMigration';
import { isRowlessProject } from '@/app/quickRunProject';
import { modelFolderKey } from '@/app/services/storage/runArtifacts';
import { showToast } from '@/shared/lazyToast';

/**
 * 삭제 결과. boolean 이 아니라 판별 가능한 형태로 두는 이유는, 서버가 거절을
 * **예외가 아니라 정상 응답**(`deleted=false`)으로 돌려주기 때문이다. boolean
 * 이면 호출자가 "지워지지 않음" 을 성공으로 오인하거나 통신 실패와 구분하지
 * 못한다 (vsmr-sim-bff#59).
 */
export type DeleteProjectOutcome =
  | { status: 'deleted' }
  | { status: 'needs-force'; remainingFiles: number }
  | { status: 'error'; message: string };

interface ProjectState {
  // 상태
  /** 목록 항목은 본문 없는 요약이다 — 본문은 currentProject(열린 프로젝트)와 fetchProjectById 로만 */
  projects: ProjectSummary[];
  currentProject: Project | null;
  currentModel: Model | null;  // 현재 선택된 모델
  loading: boolean;
  saving: boolean;  // 저장 작업용 별도 상태
  error: string | null;

  // 프로젝트 액션
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<Project | null>;
  /**
   * Read-only fetch — currentProject/loading/error 상태에 영향 없음.
   * restartBaseline hydrate 등 현재 프로젝트 컨텍스트를 건드리면 안 되는 용도로 사용.
   */
  fetchProjectById: (id: string) => Promise<Project | null>;
  createProject: (project: ProjectInsert) => Promise<Project | null>;
  /** 프로젝트 메타 갱신(이름·설명 등). **data 는 받지 않는다** — 전체 데이터 쓰기는 updateProjectData 로만 */
  updateProject: (id: string, updates: Omit<ProjectUpdate, 'data'>) => Promise<boolean>;
  /**
   * 프로젝트 데이터 쓰기 — 호출자는 "무엇을 바꿀지"(patch)만 넘기고, 페이로드는 쓰기 레인 **실행 시점**의 최신
   * 데이터 위에 만든다. 백엔드가 ProjectData 전체를 치환하므로 호출자가 미리 만든 스냅샷은 앞선 쓰기(모델·HUD
   * 갱신)의 결과를 되돌린다 — 그래서 API 가 스냅샷을 받지 않는다(리뷰 #388 R16)
   *
   * `patch` 는 **멱등**이어야 한다 — 발신 페이로드와 왕복 뒤 반영(`putEdited`)이 각각 한 번씩 계산하는데,
   * 반영의 바탕에 **그 편집이 이미 들어와 있을 수 있다**(왕복 중 도착한 `fetchProject` 응답이 서버 커밋을
   * 실어 온 경우). 그때 배열에 무조건 append 하는 patch 는 항목을 둘로 만든다 — 평범한 왕복에서는 둘 다
   * «패치 전» 데이터 위라 한 번 얹힌 것과 같으므로, 그 경로만 시험하면 안전해 보인다(신원으로 거르려면
   * `appendOnce`). 치환(`{...data, coSimConfig}`)은 그대로 안전하다.
   */
  updateProjectData: (id: string, patch: (data: ProjectData) => ProjectData) => Promise<boolean>;
  deleteProject: (id: string, force?: boolean) => Promise<DeleteProjectOutcome>;
  setCurrentProject: (project: Project | null) => void;
  clearError: () => void;

  // 모델 액션 (MAIN-001, MDH-001)
  createModel: (projectId: string, model: ModelInsert) => Promise<Model | null>;
  /**
   * 모델 업데이트. `updates` 가 함수면 **발신 시점의 모델**에서 갱신 필드를 만든다 — 그 모델의 다른 필드에서 유도하는
   * 갱신(설정 위에 HUD 키만 얹기, 원문에 신원 싣기)이 앞서 대기한 쓰기의 결과를 옛 값으로 되돌리지 않게.
   */
  updateModel: (projectId: string, modelId: string, updates: ModelUpdateInput) => Promise<boolean>;
  /** 로컬 스토어에만 모델 settings 를 반영(낙관적) — 서버 저장이 대기 중인 동안 재하이드레이션이 옛 설정을 읽지 않게 */
  /**
   * 모델 업데이트 본체 — 실패를 예외로 알린다(store.error 미사용). 저장 실패를 자기 UI(대화상자 등)에서
   * 보이는 호출처용 — 전역 error 는 로드 실패 화면이 쓰는 슬롯이라 페이지가 갈아 끼워지는 부작용을 피한다.
   * updateModel 은 이것을 감싼 불리언+store.error 판(에디터 스낵바 등 기존 계약).
   */
  updateModelOrThrow: (projectId: string, modelId: string, updates: ModelUpdateInput) => Promise<void>;
  deleteModel: (projectId: string, modelId: string) => Promise<boolean>;
  setCurrentModel: (model: Model | null) => void;
  getModel: (projectId: string, modelId: string) => Model | null;
  /**
   * 텍스트 모드 에디터에서 사용자 입력 시 in-memory 상태만 업데이트.
   * 백엔드 저장은 별도 명시적 save 시점에 수행.
   */
  updateModelTextContent: (modelId: string, textContent: string) => void;
  /** 활성 GUI 모델의 in-memory 편집 데이터(useStore)를 currentProject.data.models에 commit. DB 저장 안 함. */
  flushActiveModelChanges: (modelId: string, partial: ModelUpdate) => void;

  // 히스토리 액션
  addVersionEntry: (projectId: string, modelId: string | null, entry: Omit<VersionEntry, 'timestamp' | 'id'>) => Promise<boolean>;

  // 유틸리티
  getProjectSummaries: () => ProjectSummary[];
  getProjectModels: (projectId: string) => Model[];
}

// UUID 생성 헬퍼 — insecure context 폴백은 shared/uuid가 SSOT
const generateId = generateUuid;

/** 프로젝트 본문 읽기 — 서버의 유일한 읽기 입구. 행 없는 프로젝트(`ROWLESS_PROJECTS`, 퀵런)는 서버에 행이 없으므로
 *  묻지 않고 «없음»(null)이다 — 물으면 id 가 UUID 가 아니라 400 으로 돌아와 콘솔 오류와 실패 요청만 남는다
 *  (5차 베타 실측: 퀵런 실행 화면에 들어갈 때마다). 이 저장소의 읽기 셋이 전부 여기를 지난다. */
const readProjectRow = async (id: string): Promise<Project | null> => {
  if (isRowlessProject(id)) return null;
  const backend = await getBackend();
  return backend.projects.getProject(id);
};

/** fetchProject 요청 순번(프로젝트별) + 전체 마지막 조회 — 옛 응답이 새 응답이나 다른 프로젝트를 덮지 않게 */
const fetchProjectSeq = new Map<string, number>();
let latestFetchProjectSeq = 0;

/**
 * 프로젝트 사본은 하나 — `projects[]` 가 원본이고 `currentProject` 는 그중 현재 것을 가리키는 같은 객체다.
 * 모든 «갱신» 은 이 함수로만: 목록에 있으면 그 항목을 교체하고, 현재 프로젝트면 같은 객체로 맞춘다.
 * «교체·해제»(조회 실패·삭제·선택 변경)는 `setCurrent` 가 지킨다 — 둘 다 `currentModelFor` 를 쓴다.
 * 목록에 없는 프로젝트(다른 워크스페이스·템플릿 등 URL 로 직접 연 것)는 목록에 끼워 넣지 않는다 — 목록은 서버가
 * 준 그대로가 원칙이고, 그런 프로젝트는 currentProject 로만 든다.
 */
export function putProject(
  state: Pick<ProjectState, 'projects' | 'currentProject' | 'currentModel'>,
  next: Project,
): Pick<ProjectState, 'projects' | 'currentProject' | 'currentModel'> {
  const isCurrent = state.currentProject?.id === next.id;
  return {
    projects: state.projects.map((p) => (p.id === next.id ? summarizeProject(next, p) : p)),
    currentProject: isCurrent ? next : state.currentProject,
    currentModel: isCurrent ? currentModelFor(state, next) : state.currentModel,
  };
}

/**
 * 새 `currentProject` 에 맞춘 `currentModel`.
 *
 * `currentModel` 은 `currentProject.data.models` 의 **사본**이다. 사본을 남겨 두면 모든
    // 변이가 «둘 다 갱신하기» 를 기억해야 하고, 실제로 잊은 자리가 있었다 — `addVersionEntry`
    // 가 이력을 실어 놓고 `currentModel.updateHistory` 를 안 고쳐, 같은 세션에서 버전 제안이
    // 갱신되지 않아 **중복 버전**이 생겼다(4차 베타 6편 실측: v1.2 가 둘). 그래서 여기서 다시
    // 판다 — 이 함수의 첫 줄 주석("모든 갱신은 이 함수로만")을 `currentModel` 까지 지키게 한다.
    // 대상이 사라졌으면 null 이다(삭제도 여기서 떨어진다).
    // `null` 은 «그 프로젝트가 모델 목록을 실어 왔고(Array.isArray), 나가는 currentProject 에
    // 있던 모델이 그 목록에서 사라졌을 때» 만이다. "못 찾았다" 를 전부 "지워졌다" 로 읽으면
    // ① `data` 를 안 실은 응답(워크스페이스 이동)과 ② 남의 프로젝트 모델이 current 인 창
 * (`fetchProject` 의 후발 오버라이드로 생긴다)에서 멀쩡한 모델이 사라진다(게이트 리뷰 #547).
 *
 * 프로젝트 자체가 없어지면(`next === null`) 모델도 없다 — 그 자리를 안 지키면 삭제·404·버리기
 * 뒤에 «사라진 프로젝트의 모델»이 남아 화면과 다음 저장 페이로드에 실린다(게이트 리뷰 #547 R7).
 */
function currentModelFor(
  state: Pick<ProjectState, 'currentProject' | 'currentModel'>,
  next: Project | null,
): Model | null {
  if (!next) return null;
  // 다른 프로젝트로 «바뀌는» 중이면 새 모델이 아직 정해지지 않았다 — 셸의 하이드레이션이
  // `setCurrentModel` 로 정한다. 그 사이 null 로 비우면 편집기가 한 프레임 비므로 옛 값을 들고
  // 있는다(화면은 `EditorShell` → `editorBodyRegistry` 가 「모델을 찾을 수 없습니다」로 막는다).
  // 위 ② 와는 다른 이유다 — ② 는 `putProject` 경로의 이야기고 이 갈래는 `setCurrent` 에서만 돈다.
  if (next.id !== state.currentProject?.id) return state.currentModel;
  if (!state.currentModel || !Array.isArray(next.data?.models)) return state.currentModel;
  if (!(state.currentProject?.data?.models?.some((m) => m.id === state.currentModel!.id) ?? false)) return state.currentModel;
  return next.data!.models!.find((m) => m.id === state.currentModel!.id) ?? null;
}

/**
 * `currentProject` 를 갈아 끼우거나 내리는 유일한 자리 — `currentModel` 을 같은 규칙으로 맞춘다.
 * `putProject` 가 «갱신» 쪽을, 이쪽이 «교체·해제» 쪽을 지킨다.
 */
function setCurrent(
  state: Pick<ProjectState, 'currentProject' | 'currentModel'>,
  next: Project | null,
): Pick<ProjectState, 'currentProject' | 'currentModel'> {
  return { currentProject: next, currentModel: currentModelFor(state, next) };
}

/**
 * 같은 신원의 레코드가 이미 있으면 그대로 둔다 — 얹기는 **멱등**이어야 한다.
 *
 * `putEdited` 가 얹는 바탕에 서버 커밋이 이미 들어와 있을 수 있다: 우리 쓰기의 응답을 기다리는
 * 동안 도착한 `fetchProject` 응답이 커밋된 데이터를 실어 온다(조회는 쓰기 레인을 타지 않는다).
 * 그때 무조건 append 하면 같은 이력·같은 모델이 둘이 된다.
 *
 * 신원 없는 옛 레코드는 새 레코드(`id` 를 항상 싣는다)와 절대 같지 않다.
 */
function appendOnce<T extends { id?: string }>(list: T[] | undefined, item: T & { id: string }): T[] {
  return (list ?? []).some((x) => x.id === item.id) ? [...(list ?? [])] : [...(list ?? []), item];
}

/** 이력 한 건을 얹은 데이터 */
function withVersionEntry(data: ProjectData | null | undefined, modelId: string | null, entry: VersionEntry & { id: string }): ProjectData {
  return modelId
    ? { ...data, models: (data?.models ?? []).map((m) =>
        m.id === modelId ? { ...m, updateHistory: appendOnce(m.updateHistory, entry) } : m) }
    : { ...data, updateHistory: appendOnce(data?.updateHistory, entry) };
}

/**
 * 저장 왕복이 끝난 뒤 같은 편집을 **지금** 상태에 얹는다 — 편집은 `edit` 한 곳에만 적는다.
 *
 * 발신 페이로드도 «반영» 도 같은 `edit` 를 쓰기 때문에 둘이 갈라질 자리가 없다(갈라진 사본이
 * #547 의 원인이었다).
 *
 * 얹을 바탕은 둘 중 하나다. 아직 그 프로젝트에 머물러 있으면 **발신 전 스냅샷이 아니라** 지금의
 * 사본 — 왕복 중 들어온 in-memory 편집(`updateModelTextContent`·`flushActiveModelChanges`)이
 * 되돌아가지 않게. `currentModel` 이 파생이 된 뒤로는 그 되돌림이 «화면의 텍스트»까지 되돌린다
 * (게이트 리뷰 #547). `updateModelOrThrow` 가 같은 이유로 set 안에서 다시 판다(리뷰 #388 R9).
 * 떠났으면 발신 전 사본 — 목록 요약(model_count 등)은 그래도 갱신해야 하고, 그때는 `isCurrent`
 * 가 거짓이라 `currentProject`·`currentModel` 은 건드리지 않는다.
 *
 * `sent` 는 **편집 전** 사본을 그대로 넘긴다 — 얹는 것은 이 함수 몫이라, 이미 얹은 것을 넘기면
 * 두 번 얹힌다. 그리고 `edit` 는 **멱등**이어야 한다(`appendOnce`) — 바탕에 서버 커밋이 이미
 * 들어와 있을 수 있다.
 *
 * `row` 는 응답을 돌려주는 쓰기가 넘긴다 — 행의 나머지(`updated_at` 등)는 서버가 정본이다.
 * 본문(`data`)은 그때도 바탕 위에 얹는다(응답 본문으로 덮으면 왕복 중 편집이 되돌아간다).
 */
function putEdited(
  state: Pick<ProjectState, 'projects' | 'currentProject' | 'currentModel'>,
  projectId: string,
  sent: Project,
  edit: (data: ProjectData | null | undefined) => ProjectData,
  row?: Project,
): Pick<ProjectState, 'projects' | 'currentProject' | 'currentModel' | 'saving'> {
  const base = reflectionBase(state, projectId, sent);
  return { ...putProject(state, { ...(row ?? base), data: edit(base.data) }), saving: false };
}

/**
 * 반영이 얹힐 바탕 — 머물렀으면 지금 사본, 떠났으면 발신 전 사본.
 *
 * `models[]` 를 잃은 사본 위에는 얹지 않는다. 저장소가 여러 곳에서 거부하는 비정상 데이터라
 * (`fetchProject`·`updateProjectData`), 그 위에 스프레드하면 조용한 손실이 된다.
 */
function reflectionBase(
  state: Pick<ProjectState, 'currentProject'>,
  projectId: string,
  sent: Project,
): Project {
  const live = resolveProject(state, projectId);
  return live?.data && Array.isArray(live.data.models) ? live : sent;
}

/** 본문을 가진 프로젝트는 열린 프로젝트뿐이다 — 목록 항목은 요약이라 본문 변이의 대상이 될 수 없다 */
export function resolveProject(
  state: Pick<ProjectState, 'currentProject'>,
  projectId: string,
): Project | null {
  return state.currentProject?.id === projectId ? state.currentProject : null;
}

/** 프로젝트별 쓰기 레인 — 프로젝트 전체(ProjectData)를 치환하는 모든 쓰기(모델 갱신·생성·삭제, 프로젝트 갱신·삭제,
 *  이력 추가)가 같은 프로젝트에 대해 순서대로 나가고, 페이로드는 앞 쓰기가 끝난 발신 시점 상태로 만든다 */
const projectWriteChains = new Map<string, Promise<unknown>>();
/**
 * 프로젝트별 **쓰기 상태** — 지금 진행 중인 쓰기 수와, 끝난 쓰기의 세대. 조회가 «쓰기와 겹쳤는가» 를
 * 이 둘로 판정한다(불변식 ⑧, #548). 둘이 각각 다른 동선을 잡는다:
 *  - `inFlight > 0` — 응답이 닿는 지금 쓰기가 진행 중이다. 쓰기 **전**이든 **도중**이든 출발한 조회가
 *    도중에 닿으면 커밋 전 응답이 그 사이 친 미저장 편집을 덮는다. 반영 규칙(`putEdited`)은 자기
 *    편집만 다시 얹으므로 그 타이핑은 아무도 못 살린다.
 *  - `done` 이 발신 때와 다르다 — 그 사이 쓰기가 끝났다. 도중 출발한 조회가 반영 **뒤** 에 닿으면
 *    방금 얹은 것을 지운다(#548 의 재현).
 * 홀짝 세대 하나로 둘을 겸하지 않는다 — 올리는 자리가 하나라도 빠지면 «영원히 진행 중» 이 되어
 * 재읽기가 끝없이 되풀이된다(게이트 리뷰 #552 변이에서 실제로 매달렸다). 수를 `finally` 로 내리면
 * 새지 않는다.
 */
const projectWrites = new Map<string, { inFlight: number; done: number }>();
/**
 * 우리 쓰기 레인이 지운 프로젝트 — 겹친 조회의 재읽기가 «없음» 을 오류로 올리지 않게. 실 백엔드의
 * `getProject` 는 없는 프로젝트에 `null` 을 돌려주지 않고 throw 한다(`Promise<Project>`,
 * `projectManagerService.getProject` 의 "response has no project"). 그러면 삭제 성공 직후 `error` 가
 * 서서 목록 화면이 오류로 덮인다(게이트 리뷰 #552 E). id 는 uuid 라 되살아나지 않으므로 집합은 삭제
 * 수만큼만 는다.
 */
const deletedProjectIds = new Set<string>();
const writesOf = (projectId: string) => {
  let w = projectWrites.get(projectId);
  if (!w) { w = { inFlight: 0, done: 0 }; projectWrites.set(projectId, w); }
  return w;
};
function enqueueProjectWrite<T>(projectId: string, fn: () => Promise<T>): Promise<T> {
  const prev = projectWriteChains.get(projectId) ?? Promise.resolve();
  const guarded = async () => {
    const w = writesOf(projectId);
    w.inFlight += 1;
    try { return await fn(); } finally { w.inFlight -= 1; w.done += 1; }
  };
  const run = prev.then(guarded, guarded);
  projectWriteChains.set(projectId, run.catch(() => undefined));
  return run;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  // 초기 상태
  projects: [],
  currentProject: null,
  currentModel: null,
  loading: false,
  saving: false,
  error: null,

  /**
   * 프로젝트 목록 조회
   */
  fetchProjects: async () => {
    set({ loading: true, error: null });

    try {
      const backend = await getBackend();
      const projects = await backend.projects.listProjects();

      // 목록의 현재 프로젝트 행은 지금 들고 있는 객체로 — 사본은 하나여야 하고, 에디터가 메모리에만 얹어 둔
      // 편집(저장 전 텍스트 등)이 백그라운드 목록 조회에 날아가면 안 된다(종전에도 목록 조회는 현재 프로젝트를
      // 건드리지 않았다). 현재 프로젝트의 최신은 fetchProject 가 가져온다
      set({ projects, loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : '프로젝트 목록을 불러오는데 실패했습니다';
      set({ error: message, loading: false });
      console.error('[ProjectStore] fetchProjects error:', error);
    }
  },

  /**
   * 단일 프로젝트 조회.
   *
   * NOTE (2026-04-28): legacy 자동 마이그레이션 + 헬퍼 일체 제거됨. 과거 사고
   * 이력 — top-level nodes/edges 가 있고 models[] 없는 row 를 자동으로 "Default
   * Model" 한 개로 wrap 후 DB 에 덮어쓰던 silent 동작이 멀티모델 프로젝트를
   * 통째로 날리는 데이터 손실 사고를 만듦. 안전망으로 inline 체크를 남겨,
   * 이미 손상된 row 가 DB 에 있으면 명시 에러로 surface 하고 로드를 중단한다.
   */
  fetchProject: async (id: string) => {
    set({ loading: true, error: null });
    const seq = (fetchProjectSeq.get(id) ?? 0) + 1;
    fetchProjectSeq.set(id, seq);
    const overall = ++latestFetchProjectSeq;
    // 같은 프로젝트의 늦게 도착한 옛 응답이 새 응답을 덮어쓰지 않게 — 요청 순서상 마지막 것만 반영하고, 밀린
    // 요청은 null(호출처의 `if (project)` 가드가 후속 작업을 건너뛴다)
    const isStale = () => seq !== fetchProjectSeq.get(id);
    // 그 사이 다른 프로젝트를 조회했으면(빠른 화면 전환) 데이터는 목록에 반영하되 currentProject 는 건드리지 않는다
    const isLatestOverall = () => overall === latestFetchProjectSeq;
    // 쓰기와 겹친 조회는 권위가 없다(불변식 ⑧). 응답이 커밋 전인지는 «발신 시점» 이 정하지 지금
    // 들고 있는지가 정하지 않는다. 겹침 = 응답 시점에 쓰기가 진행 중이거나, 발신 뒤에 쓰기가 끝났다.
    // 쓰기 도중 닿은 응답은 필요한 적이 없고(반영 규칙이 어차피 같은 편집을 얹는다) 커밋 전이면
    // 그 사이 친 편집을 덮으므로 해롭다 — 게이트 리뷰 #552 프로브 A.
    let doneAtIssue = writesOf(id).done;
    const overlappedWrite = () => { const w = writesOf(id); return w.inFlight > 0 || w.done !== doneAtIssue; };

    try {
      let data: Project | null = null;
      for (;;) {
        // 겹친 조회는 응답이 **값이든 실패든** 권위가 없다 — 서버는 삭제 전에 보낸 조회를 삭제 뒤에 처리할 수
        // 있어서 «없음» throw 가 재읽기가 아니라 첫 응답으로 닿는다(게이트 리뷰 #552 F). 겹치지 않은 실패만
        // 진짜 실패다 — 바깥 catch 로 나가 `error` 가 선다.
        let failed = false; let failure: unknown;
        try { data = await readProjectRow(id); } catch (error) { failed = true; failure = error; }
        if (isStale()) return null;
        if (!overlappedWrite()) { if (failed) throw failure; break; }
        // 이미 들고 있으면 그 사본이 더 새롭다 — 돌려준다. `null` 이 아니다(`useModelHome` 이 null 을 «로드 실패» 로 읽는다).
        const held = resolveProject(get(), id);
        if (held) { set({ loading: false }); return held; }
        // 들고 있지 않아도 이 응답은 커밋 전일 수 있다 — 기록 중 다른 프로젝트를 들렀다 돌아오면 이력이
        // 빠진 사본이 올라오고(#548 재현), 삭제 전에 출발한 조회는 지운 프로젝트를 되살린다(게이트 리뷰
        // #552 프로브 B·C). 그 프로젝트의 쓰기 레인이 빈 뒤 **이 호출 안에서** 다시 읽는다(우리가 지운 것이면
        // 다시 읽지 않고 null) — `fetchProject`
        // 를 다시 부르면 seq·overall 이 새로 매겨져, 기다리는 동안 사용자가 연 다른 프로젝트를 이 프로젝트로
        // 갈아 끼운다(프로브 D, 회귀). 루프를 나가면 원래 적용 경로(손상 검사·isLatestOverall)를 그대로 탄다.
        await projectWriteChains.get(id);
        // 그 레인이 이 프로젝트를 지웠으면 다시 읽을 것이 없다 — 실 백엔드는 없는 프로젝트에 throw 하므로
        // 다시 읽으면 삭제 성공 뒤 `error` 가 선다. `setCurrent(null)` 은 하지 않는다 — 삭제가 이미 비웠고,
        // 기다리는 동안 사용자가 연 다른 프로젝트를 지우면 안 된다.
        if (deletedProjectIds.has(id)) { set({ loading: false }); return null; }
        // 재읽기의 «발신 시점» 을 다시 잡는다 — `overlappedWrite` 가 이 변수를 닫아 들고 있어, 이 뒤로는 새로 끝난 쓰기만 겹침이다.
        doneAtIssue = writesOf(id).done;
      }

      // Pre-existing 손상 데이터 안전망 — 신구조면 항상 models 키가 존재 (빈
      // 배열이라도). models 가 array 가 아니면 손상으로 간주.
      const projectData = data?.data;
      if (data && projectData && !Array.isArray((projectData as { models?: unknown }).models)) {
        const keys = Object.keys(projectData).join(', ');
        const msg =
          '비정상 형식의 프로젝트 데이터가 감지되었습니다 (models[] 부재). ' +
          '백업에서 복원하거나 수동으로 데이터를 점검해주세요. ' +
          `(project=${id}, data keys=[${keys}])`;
        console.error('[ProjectStore] Refusing malformed project data:', msg);
        set((state) => ({ error: msg, loading: false, ...setCurrent(state, null) }));
        return null;
      }

      // scope 정합(카탈로그 밖 id·커버리지 구멍·프로젝트 밖 담당)은 로드 시점에 경고로 남긴다 —
      // 차단하지 않는다(옛 데이터가 열려야 고칠 수 있다). 무음 방치는 금지.
      if (projectData && !scopeWarned.has(id)) {
        const check = validateProjectData(projectData);
        if (!check.valid) {
          scopeWarned.add(id);
          console.warn(`[ProjectStore] scope 정합 경고 (project=${id}):`, check.errors);
        }
      }

      if (!data) {
        set((state) => (isLatestOverall() ? { ...setCurrent(state, null), loading: false } : { loading: false }));
        return null;
      }
      set((state) => ({
        ...putProject(state, data),
        ...(isLatestOverall() ? setCurrent(state, data) : {}),
        loading: false,
      }));
      return data;
    } catch (error) {
      if (isStale()) {
        console.warn('[ProjectStore] superseded fetchProject failed (ignored):', error);
        return null;
      }
      const message = error instanceof Error ? error.message : '프로젝트를 불러오는데 실패했습니다';
      set((state) => (isLatestOverall() ? { error: message, loading: false, ...setCurrent(state, null) } : { error: message, loading: false }));
      console.error('[ProjectStore] fetchProject error:', error);
      return null;
    }
  },

  /**
   * Read-only fetch — currentProject / loading / error 를 건드리지 않음.
   * 소스 프로젝트 참조 (restart baseline hydrate 등) 용도.
   */
  fetchProjectById: async (id: string) => {
    try {
      // 읽기만 한다(DB 를 고쳐 쓰지 않는다) — 저장본 이행(`migrateProjectData`)은 읽기 길(`readProjectRow` → `toProject`)이 적용한다.
      return await readProjectRow(id);
    } catch (error) {
      console.error('[ProjectStore] fetchProjectById error:', error);
      return null;
    }
  },

  /**
   * 프로젝트 생성 (새 구조 지원)
   * @param project 생성할 프로젝트 정보
   */
  createProject: async (project: ProjectInsert) => {
    set({ loading: true, error: null });

    try {
      // 새 구조로 초기 데이터 생성. project.data 가 있으면 신구조라고 가정 —
      // legacy 자동 마이그레이션은 silent 데이터 손실 사고 후 제거됨 (2026-04-28).
      // 외부 .json 파일은 «JSON 에서 만들기»(planProjectFromFile)가 스냅샷 형식만 받아 저작 키를 싣는다.
      const initialData: ProjectData = project.data ?? {
        totalScope: {
          systems: ['primary', 'secondary', 'bop'],
          components: [],
        },
        models: [],
        updateHistory: [],
      };

      const backend = await getBackend();
      const data = await backend.projects.createProject({
        ...project,
        data: initialData,
      });

      // 목록에 추가
      set((state) => ({ projects: [summarizeProject(data), ...state.projects], loading: false }));

      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : '프로젝트 생성에 실패했습니다';
      set({ error: message, loading: false });
      console.error('[ProjectStore] createProject error:', error);
      return null;
    }
  },

  /**
   * 프로젝트 업데이트
   * @param id 프로젝트 ID
   * @param updates 업데이트할 필드
   */
  updateProject: (id: string, updates: Omit<ProjectUpdate, 'data'>) =>
    enqueueProjectWrite(id, async () => {
    // saving 상태 사용 (loading과 분리하여 UI 깜빡임 방지)
    set({ saving: true, error: null });

    try {
      const backend = await getBackend();
      const data = await backend.projects.updateProject(id, updates);

      // 목록 및 현재 프로젝트 업데이트
      set((state) => ({ ...putProject(state, data), saving: false }));

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '프로젝트 업데이트에 실패했습니다';
      set({ error: message, saving: false });
      console.error('[ProjectStore] updateProject error:', error);
      return false;
    }
  }),

  updateProjectData: (id: string, patch: (data: ProjectData) => ProjectData) =>
    enqueueProjectWrite(id, async () => {
    set({ saving: true, error: null });
    try {
      const backend = await getBackend();
      // 실행 시점의 최신 데이터 — 앞선 쓰기의 결과(응답 반영)가 이미 들어 있다.
      // 열려 있지 않은 프로젝트에도 쓸 수 있어야 한다(예제 갱신은 목록에서 바로
      // 고친다). 목록 항목은 본문 없는 요약이므로 그때는 본문을 받아 온다 —
      // 스냅샷을 호출자가 넘기지 않는 이유(리뷰 #388 R16)는 그대로다.
      const current = resolveProject(get(), id) ?? (await readProjectRow(id));
      const data = current?.data;
      // models[] 가드 — 비정상 데이터 위에 스프레드하면 models[] 가 사라지는 조용한 손실(2026-04-28)
      if (!current || !data || !Array.isArray((data as { models?: unknown }).models)) {
        console.error('[ProjectStore] updateProjectData refused: no valid project data', { id });
        set({ saving: false });
        return false;
      }
      const next = await backend.projects.updateProject(id, { data: patch(data) });
      // 응답 본문으로 덮으면 왕복 중 들어온 in-memory 편집(`updateModelTextContent`)이 되돌아간다 —
      // 네 쓰기 액션과 같은 규칙으로 «지금» 상태 위에 다시 얹는다. 행의 나머지는 응답이 정본이다.
      // 캐스트가 안전한 근거는 두 갈래다 — 바탕이 `current` 면 바로 위의 `models[]` 가드가, `live` 면
      // `reflectionBase` 의 `Array.isArray(live.data.models)` 가 본문을 보장한다.
      set((state) => putEdited(state, id, current, (d) => patch(d as ProjectData), next));
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '프로젝트 업데이트에 실패했습니다';
      set({ error: message, saving: false });
      console.error('[ProjectStore] updateProjectData error:', error);
      return false;
    }
  }),

  /**
   * 프로젝트 삭제
   * @param id 프로젝트 ID
   */
  deleteProject: (id: string, force = false): Promise<DeleteProjectOutcome> =>
    enqueueProjectWrite(id, async (): Promise<DeleteProjectOutcome> => {
    set({ loading: true, error: null });

    try {
      const backend = await getBackend();
      const result = await backend.projects.deleteProject(id, force);

      // 서버가 거절한 경우 — 아직 아무것도 지워지지 않았으므로 목록을 건드리지
      // 않는다. 호출자가 개수를 보여주고 force 로 재요청할지 정한다.
      if (!result.deleted) {
        set({ loading: false });
        return { status: 'needs-force', remainingFiles: result.remainingFiles };
      }

      // 목록에서 제거 및 현재 프로젝트 초기화. 지운 id 를 기억한다 — 겹친 조회의 재읽기가 없는 프로젝트를
      // 다시 읽지 않게(불변식 ⑧).
      deletedProjectIds.add(id);
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        ...(state.currentProject?.id === id ? setCurrent(state, null) : {}),
        loading: false,
      }));

      return { status: 'deleted' };
    } catch (error) {
      const message = error instanceof Error ? error.message : '프로젝트 삭제에 실패했습니다';
      set({ error: message, loading: false });
      console.error('[ProjectStore] deleteProject error:', error);
      return { status: 'error', message };
    }
  }),

  /**
   * 현재 프로젝트 설정
   */
  setCurrentProject: (project: Project | null) => {
    set((state) => setCurrent(state, project));
  },

  /**
   * 에러 초기화
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * 프로젝트 요약 정보 반환 (목록 표시용)
   */
  getProjectSummaries: (): ProjectSummary[] => get().projects,

  // ============================================
  // 모델 액션 (MAIN-001, MDH-001)
  // ============================================

  /**
   * 모델 생성
   * @param projectId 프로젝트 ID
   * @param model 생성할 모델 정보
   */
  createModel: (projectId: string, model: ModelInsert) =>
    enqueueProjectWrite(projectId, async () => {
    set({ saving: true, error: null });

    try {
      const project = resolveProject(get(), projectId);
      if (!project) {
        throw new Error('프로젝트를 찾을 수 없습니다');
      }

      const now = new Date().toISOString();
      const newModel: Model = {
        id: generateId(),
        name: model.name,
        analysisCodes: model.analysisCodes,
        description: model.description ?? null,
        scope: model.scope,
        ...roleFields(model.role),
        nodes: model.nodes ?? [],
        edges: model.edges ?? [],
        settings: model.settings ?? {},
        updateHistory: [],
        created_at: now,
        updated_at: now,
        // 원문은 종류·형태와 무관하게 준 대로 싣는다 — 통합 모드에서 원문이 SSOT다
        ...(model.textContent !== undefined ? { textContent: model.textContent } : {}),
      };

      const withNewModel = (data: ProjectData | null | undefined): ProjectData => ({
        ...data,
        models: appendOnce(data?.models, newModel),
      });

      const backend = await getBackend();
      await backend.projects.updateProject(projectId, { data: withNewModel(project.data) });

      set((state) => putEdited(state, projectId, project, withNewModel));

      return newModel;
    } catch (error) {
      const message = error instanceof Error ? error.message : '모델 생성에 실패했습니다';
      set({ error: message, saving: false });
      console.error('[ProjectStore] createModel error:', error);
      return null;
    }
  }),

  /**
   * 모델 업데이트 — 실패는 false + store.error (에디터 스낵바 등 기존 계약). 본체는 updateModelOrThrow.
   * @param projectId 프로젝트 ID
   * @param modelId 모델 ID
   * @param updates 업데이트할 필드
   */
  updateModel: async (projectId: string, modelId: string, updates: ModelUpdateInput) => {
    set({ error: null });
    try {
      await get().updateModelOrThrow(projectId, modelId, updates);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '모델 업데이트에 실패했습니다';
      set({ error: message });
      console.error('[ProjectStore] updateModel error:', error);
      return false;
    }
  },

  /**
   * 모델 업데이트 본체 — 실패는 예외(store.error 미사용). 저장 실패를 자기 UI 에서 보이는 호출처용.
   */

  updateModelOrThrow: (projectId: string, modelId: string, input: ModelUpdateInput) =>
    // 같은 프로젝트의 쓰기는 **직렬** — 백엔드는 ProjectData 전체를 치환하므로(backend/oidc.ts) 동시 쓰기는
    // 늦게 닿는 쪽이 앞 쪽의 다른 모델 변경을 되돌린다. 페이로드는 앞 쓰기가 끝난 **발신 시점** 상태로 만든다
    enqueueProjectWrite(projectId, async () => {
    set({ saving: true });
    try {
      const project = resolveProject(get(), projectId);
      if (!project?.data?.models) {
        throw new Error('프로젝트를 찾을 수 없습니다');
      }

      const modelIndex = project.data.models.findIndex((m) => m.id === modelId);
      if (modelIndex === -1) {
        throw new Error('모델을 찾을 수 없습니다');
      }

      const updates = typeof input === 'function' ? input(project.data.models[modelIndex]) : input;
      const updatedModel: Model = {
        ...project.data.models[modelIndex],
        ...updates,
        updated_at: new Date().toISOString(),
      };

      const updatedModels = [...project.data.models];
      updatedModels[modelIndex] = updatedModel;

      const updatedData: ProjectData = {
        ...project.data,
        models: updatedModels,
      };

      const backend = await getBackend();
      await backend.projects.updateProject(projectId, { data: updatedData });

      // 상태 업데이트 — **지금** 상태 위에 반영한다. 요청 전 스냅샷(project)을 그대로 되쓰면 대기 중
      // 로컬에서 더 새로 바뀐 필드(HUD 자동 저장의 낙관 반영 등)가 옛 값으로 되돌아간다(리뷰 #388 R9).
      // 갱신 필드는 캡처 시점과 같을 때만 응답값으로, 그 사이 바뀐 필드는 로컬(더 새 것)을 지킨다.
      set((state) => {
        const current = resolveProject(state, projectId);
        const currentModels = current?.data?.models;
        const currentIndex = currentModels?.findIndex((m) => m.id === modelId) ?? -1;
        if (!current?.data || !currentModels || currentIndex === -1) return { saving: false }; // 그새 사라진 대상은 되살리지 않는다
        const currentModel = currentModels[currentIndex];
        const captured = project.data!.models![modelIndex];
        const merged: Model = { ...currentModel, updated_at: updatedModel.updated_at };
        for (const key of Object.keys(updates) as Array<keyof ModelUpdate>) {
          if (currentModel[key] === captured[key]) (merged as unknown as Record<string, unknown>)[key] = updatedModel[key];
        }
        const models = currentModels.map((m, i) => (i === currentIndex ? merged : m));
        return {
          ...putProject(state, { ...current, data: { ...current.data!, models } }),
          saving: false,
        };
      });

    } catch (error) {
      set({ saving: false });
      throw error;
    }
    }),

  /**
   * 모델 삭제
   * @param projectId 프로젝트 ID
   * @param modelId 모델 ID
   */
  deleteModel: (projectId: string, modelId: string) =>
    enqueueProjectWrite(projectId, async () => {
    set({ saving: true, error: null });

    try {
      const project = resolveProject(get(), projectId);
      if (!project?.data?.models) {
        throw new Error('프로젝트를 찾을 수 없습니다');
      }

      // 시나리오 동작은 모델 id 를 담는다 — 지운 모델을 가리키는 동작도 함께 뺀다(남기면 시작이 «대상 모델이 프로젝트에 없습니다» 로 막힌다)
      const withoutModel = (data: ProjectData | null | undefined): ProjectData => ({
        ...data,
        models: (data?.models ?? []).filter((m) => m.id !== modelId),
        ...(data?.scenarios
          ? { scenarios: data.scenarios.map((s) => ({ ...s, actions: s.actions.filter((a) => a.modelId !== modelId) })) }
          : {}),
      });

      const backend = await getBackend();
      await backend.projects.updateProject(projectId, { data: withoutModel(project.data) });

      set((state) => putEdited(state, projectId, project, withoutModel));

      // 그 모델의 폴더(빌드 캐시)를 지운다 — 모델이 없어지면 그 폴더를 지울 사건이 더는 없다. 실행이 읽는 것은 실행 폴더라
      // 지워도 지난 실행은 그대로다(docs/DESIGN-run-input-lifecycle.md §6.7). 모델 삭제는 이미 됐으므로 실패는 알리기만 한다
      // 자리의 소유자 — 판정과 사유는 storageOwner 하나. 모르면 지우지 않고, 지우지 못한 것은 다른 실패와 같이 알린다
      const notDeleted = (why: string) =>
        showToast(`모델은 지웠지만 저장된 모델 파일을 지우지 못했습니다: ${why}`, { icon: '⚠️', duration: 8000 });
      const { storageOwner } = await import('@/app/services/storage/signedInStorageOwner');
      const owner = storageOwner('모델 파일을 지울', project);
      if (owner.ok) {
        try {
          const { storageService } = await import('@/app/services/storage/storageService');
          await storageService.deleteFolder(modelFolderKey({ userId: owner.userId, projectId, modelId }));
        } catch (err) {
          console.warn('[ProjectStore] deleteModel: 모델 폴더를 지우지 못함', err);
          notDeleted(err instanceof Error ? err.message : String(err));
        }
      } else {
        console.warn('[ProjectStore] deleteModel: 모델 폴더를 지우지 못함 —', owner.reason, projectId, modelId);
        notDeleted(owner.reason);
      }

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '모델 삭제에 실패했습니다';
      set({ error: message, saving: false });
      console.error('[ProjectStore] deleteModel error:', error);
      return false;
    }
  }),

  /**
   * 현재 모델 설정
   */
  setCurrentModel: (model: Model | null) => {
    set({ currentModel: model });
  },

  /**
   * 프로젝트에서 모델 가져오기
   * @param projectId 프로젝트 ID
   * @param modelId 모델 ID
   */
  getModel: (projectId: string, modelId: string): Model | null => {
    const project = resolveProject(get(), projectId);
    return findModelById(project?.data ?? {}, modelId) ?? null;
  },

  // ============================================
  // 히스토리 액션
  // ============================================

  /**
   * 버전 엔트리 추가
   * @param projectId 프로젝트 ID
   * @param modelId 모델 ID (null이면 프로젝트 레벨)
   * @param entry 버전 엔트리 (timestamp 제외)
   */
  addVersionEntry: (projectId: string, modelId: string | null, entry: Omit<VersionEntry, 'timestamp' | 'id'>) =>
    enqueueProjectWrite(projectId, async () => {
    set({ saving: true, error: null });

    try {
      const project = resolveProject(get(), projectId);
      if (!project?.data) {
        throw new Error('프로젝트를 찾을 수 없습니다');
      }

      const fullEntry: VersionEntry & { id: string } = {
        ...entry,
        id: generateId(),
        timestamp: new Date().toISOString(),
      };

      if (modelId && !(project.data.models ?? []).some((m) => m.id === modelId)) {
        throw new Error('모델을 찾을 수 없습니다');
      }
      const withVersion = (data: ProjectData | null | undefined): ProjectData =>
        withVersionEntry(data, modelId, fullEntry);

      const backend = await getBackend();
      await backend.projects.updateProject(projectId, { data: withVersion(project.data) });

      set((state) => putEdited(state, projectId, project, withVersion));

      // 왕복 중 대상 모델이 이 화면에서 사라져도(다른 창의 삭제가 조회로 들어온다) **기록은 됐다** —
      // 발신 페이로드는 발신 전 스냅샷 전체이고 백엔드가 `ProjectData` 를 통째로 치환하므로, 우리
      // 쓰기가 그 모델을 서버에서 되살리며 이력까지 실어 보냈다(게이트 리뷰 #547 R4 실측: payload
      // models=["m1"] hist=["1.0"]). 여기서 `false` 를 돌려주면 화면이 «저장이 반영되지 않았습니다»
      // 라는 거짓을 말하고, 그 말을 믿고 다시 기록하면 `id` 가 달라 `appendOnce` 가 못 걸러 **진짜
      // 중복**이 된다. 남는 것은 이 화면 사본만 낡았다는 것인데, 모델이 사라진 사실은 화면에 이미
      // 보인다(`currentModel` 이 null 이 된다). 사본을 서버에 맞추려면 조회·쓰기 순서 계약이 필요하다.
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '버전 히스토리 추가에 실패했습니다';
      set({ error: message, saving: false });
      console.error('[ProjectStore] addVersionEntry error:', error);
      return false;
    }
  }),


  /**
   * 프로젝트의 모델 목록 반환
   * @param projectId 프로젝트 ID
   */
  getProjectModels: (projectId: string): Model[] => {
    const project = resolveProject(get(), projectId);
    return project?.data?.models ?? [];
  },

  /**
   * 텍스트 모드 에디터 입력 내용을 in-memory 상태에만 반영.
   * 백엔드 저장은 별도 명시적 save 시점에 수행.
   * @param modelId 대상 모델 ID
   * @param textContent 업데이트할 텍스트 내용
   */
  updateModelTextContent: (modelId: string, textContent: string) => {
    // 재시작 모델은 원문을 갖지 않는다(설계 §4.1-1). 원문이 생기면 열 때마다
    // 덱에서 그래프를 다시 파고, 그 파생이 노드 id 를 새로 지어 저장된
    // 기준선과 신원이 갈린다 — 편집 없이 전 컴포넌트가 삭제로 잡힌다(#460).
    // 표면(덱 열기 버튼)이 이미 막으므로 여기 도달하면 버그다: 조용히
    // 무시하지 않고 드러낸다.
    const target = get().currentProject?.data?.models?.find((m) => m.id === modelId);
    if (isRestartMode({ taskMode: undefined, globalSettings: target?.settings as never })) {
      throw new Error(
        `재시작 모델에는 덱 원문을 둘 수 없습니다 (model=${modelId}) — ` +
          '재시작이 내는 것은 실행 때 생성되는 오버라이드 덱뿐입니다',
      );
    }
    set((state) => {
      const project = state.currentProject;
      if (!project?.data?.models) return state;

      const now = new Date().toISOString();
      const models = project.data.models.map((m) =>
        m.id === modelId
          ? { ...m, textContent, updated_at: now }
          : m
      );

      const updatedProject: Project = {
        ...project,
        data: { ...project.data, models },
      };

      return {
        ...putProject(state, updatedProject),
      };
    });
  },

  flushActiveModelChanges: (modelId: string, partial: ModelUpdate) => {
    set((state) => {
      const project = state.currentProject;
      if (!project?.data?.models) return state;

      const now = new Date().toISOString();
      const models = project.data.models.map((m) =>
        m.id === modelId
          ? { ...m, ...partial, updated_at: now }
          : m
      );

      const updatedProject: Project = {
        ...project,
        data: { ...project.data, models },
      };

      return {
        ...putProject(state, updatedProject),
      };
    });
  },
}));

export default useProjectStore;
